import { FormikProps } from 'formik';
import { isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components/macro';
import { useTemporaryBoolean } from '../../../helpers/hooks/useTemporaryBoolean';
import { Alert } from '../../../shared/Alert';
import { ButtonRowAlignment, PrimaryProgressButton } from '../../../shared/Button';
import { FadingMessage } from '../../../shared/FadingMessage';
import { FormButtonRow } from '../../../shared/form/Form';
import { CheckboxField } from '../../../shared/form/inputs/CheckboxField';
import {
  TopLevelValidationError,
  TopLevelValidationErrorContainer,
  topLevelValidationErrorTestId,
} from '../../../shared/form/TopLevelValidationError';
import { spacing03, spacing04 } from '../../../styling/spacing';
import { DesignFormModel } from './designFormModel';

type ModalProps = {
  warnings: Array<string>;
  setIsWarningModalOpen: (isOpen: boolean) => void;
};

export type NextStepAfterSavingDesign = 'BackToProject' | 'ConfigurePdf';

type Props = FormikProps<DesignFormModel> & {
  disabled?: boolean;
  align: ButtonRowAlignment;
  setNextStepAfterSave: (nextStep: NextStepAfterSavingDesign) => void;
} & ModalProps;

export const DesignFormButtons = ({
  disabled,
  align,
  warnings,
  setIsWarningModalOpen,
  setNextStepAfterSave,
  ...formikProps
}: Props) => {
  const isInReadOnlyMode = disabled;
  const [hasAttemptedToSubmit, setHasAttemptedToSubmit] = useState(false);

  const [isShowingSavedAsDraftMessage, showSavedAsDraftMessage] = useTemporaryBoolean();
  useEffect(() => {
    if (!isShowingSavedAsDraftMessage && formikProps.status === 'Saved as draft') {
      showSavedAsDraftMessage();
    }
  }, [formikProps.status, isShowingSavedAsDraftMessage, showSavedAsDraftMessage]);

  const shouldShowWarningModal = !formikProps.values.isDraft && !isEmpty(warnings) && isEmpty(formikProps.errors);

  const handleSaveClick = (nextStep: NextStepAfterSavingDesign) => {
    setNextStepAfterSave(nextStep);

    if (shouldShowWarningModal) {
      setIsWarningModalOpen(true);
    } else {
      setHasAttemptedToSubmit(true);
    }
  };

  return (
    <DesignFormButtonsContainer>
      {!isInReadOnlyMode && <CheckboxField name="isDraft" label="Save as draft" />}

      {!isInReadOnlyMode && hasAttemptedToSubmit && (
        <>
          <TopLevelValidationError topLevelValidationFieldName="unitsValidation" formErrors={formikProps.errors} />
          {!isEmpty(formikProps.errors) && (
            <TopLevelValidationErrorContainer>
              <Alert
                data-testid={topLevelValidationErrorTestId}
                type="warning"
                body="Some fields are invalid - please correct them before attempting to submit."
              />
            </TopLevelValidationErrorContainer>
          )}
        </>
      )}
      <FormButtonRow $align={align}>
        {!isInReadOnlyMode && (
          <>
            <PrimaryProgressButton
              type={shouldShowWarningModal ? 'button' : 'submit'}
              inProgress={formikProps.isSubmitting}
              onClick={() => handleSaveClick('BackToProject')}
            >
              {formikProps.values.isDraft ? 'Save as Draft' : 'Save'}
            </PrimaryProgressButton>
            {!formikProps.values.isDraft && (
              <PrimaryProgressButton
                type={shouldShowWarningModal ? 'button' : 'submit'}
                inProgress={formikProps.isSubmitting}
                onClick={() => handleSaveClick('ConfigurePdf')}
              >
                Save and Configure PDF
              </PrimaryProgressButton>
            )}
            {align === 'left' && (
              <SavedAsDraftMessage $underneath={false} $visible={isShowingSavedAsDraftMessage}>
                Saved as draft
              </SavedAsDraftMessage>
            )}
          </>
        )}
      </FormButtonRow>

      {align === 'right' && (
        <SavedAsDraftMessage $underneath={true} $visible={isShowingSavedAsDraftMessage}>
          Saved as draft
        </SavedAsDraftMessage>
      )}
    </DesignFormButtonsContainer>
  );
};

const DesignFormButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;

  > div {
    margin-bottom: 0;
  }
`;

const draftMessageUnderneathStyles = `
  margin-top: ${spacing04};
`;

const SavedAsDraftMessage = styled(FadingMessage)<{ $underneath: boolean }>`
  margin-left: ${spacing03};
  ${props => props.$underneath && draftMessageUnderneathStyles}
`;
